import { injectable } from 'inversify'
import { action, makeObservable, observable, runInAction } from 'mobx'
import type { FilterScope } from '@core/infra/filters/filters.config'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'

/**
 * Which rows the user has ticked, per list.
 *
 * Keyed by the same scope as the filters, for the same reason: the machinery is
 * identical everywhere and only the meaning of an id differs, which is the list
 * Presenter's business.
 *
 * Deliberately *not* in the URL, unlike the filters. A filter describes a view
 * and is worth sharing; a set of ticked rows is a half-finished action, and a
 * link that arrives with three accounts already marked for deletion is a link
 * nobody wants to receive.
 *
 * Selection is also never cleared when filters change — the Presenter intersects
 * it with the rows actually on screen, so a batch action can only ever reach
 * what the user can see. That is the safe half of the same problem, and it holds
 * without a reaction between two Repositories.
 */
@injectable()
export class SelectionRepository implements UserScopedState {
  selectedByScopePm: Readonly<Partial<Record<FilterScope, readonly string[]>>> = {}

  constructor() {
    makeObservable(this, {
      selectedByScopePm: observable.ref,
      toggle: action,
      select: action,
      clear: action,
    })
  }

  selectedFor = (scope: FilterScope): readonly string[] => this.selectedByScopePm[scope] ?? []

  isSelected = (scope: FilterScope, id: string): boolean => this.selectedFor(scope).includes(id)

  toggle = (scope: FilterScope, id: string): void => {
    const current = this.selectedFor(scope)
    this.write(scope, current.includes(id) ? current.filter((held) => held !== id) : [...current, id])
  }

  /** Replaces the whole selection — what a select-all checkbox needs. */
  select = (scope: FilterScope, ids: readonly string[]): void => {
    this.write(scope, [...ids])
  }

  clear = (scope: FilterScope): void => {
    this.write(scope, [])
  }

  private write(scope: FilterScope, ids: readonly string[]): void {
    this.selectedByScopePm = { ...this.selectedByScopePm, [scope]: ids }
  }

  resetUserScopedState = (): void => {
    runInAction(() => {
      this.selectedByScopePm = {}
    })
  }
}
